import React, { useEffect } from "react";
import { useState } from "react";
import { BsChevronDown } from "react-icons/bs";
import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";
import useNavigateToProfile from "../../hooks/useNavigateToProfile";
import { UN_REGISTERED } from "../../Constants";
import ExternalLink from "../../components/common/ExternalLink";
import WalletDisplay from "../../components/common/WalletDisplay";
import { contractNetworks, getChain } from "../../utils/currencies";
import useTokenPrices from "../../hooks/useTokenPrices";
import { HiOutlineUsers, HiOutlineExternalLink } from "react-icons/hi";

const Funders = ({ funders }) => {
  const [seeAll, setSeeAll] = useState(false);
  const [sortedFunders, setSortedFunders] = useState(funders);
  const { navigateToProfile } = useNavigateToProfile();
  const { toDollar } = useTokenPrices();
  
  useEffect(() => {
    setSortedFunders(
      [...funders]?.sort((a, b) => {
        const aDollarAmount = toDollar({
          amount: a?.amount,
          token: a?.token,
          decimal: 2,
        });
        const bDollarAmount = toDollar({
          amount: b?.amount,
          token: b?.token,
          decimal: 2,
        });
        return bDollarAmount - aDollarAmount;
      })
    );
  }, [funders]);

  const getMedalIcon = (index) => {
    if (index === 0) return "🥇";
    if (index === 1) return "🥈";
    if (index === 2) return "🥉";
    return null;
  };

  return (
    <div className="w-full flex flex-col pt-6">
      {/* Header Section */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary to-[#7e7cff] flex items-center justify-center">
          <HiOutlineUsers className="w-5 h-5 text-white" />
        </div>
        <h5 className="text-xl font-bold text-secondary">Contributors</h5>
        <span className="ml-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
          {funders?.length || 0} supporters
        </span>
      </div>
      
      <p className="text-gray-600 leading-relaxed mb-8 max-w-3xl">
        Here's a comprehensive list of the extraordinary contributors who have
        made this project possible. Thanks to their generosity and support, we're
        making significant strides in nurturing the growing DeSci community.
      </p>
      
      {!funders?.length ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <svg className="w-20 h-20 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="text-lg font-medium">No contributors yet</p>
          <p className="text-sm">Be the first to support this project!</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl shadow-md">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-white">
                  <th className="px-4 py-4 text-left font-semibold text-gray-600 w-16">Rank</th>
                  <th className="px-4 py-4 text-left font-semibold text-gray-600">Contributor</th>
                  <th className="px-4 py-4 text-left font-semibold text-gray-600">Token ID</th>
                  <th className="px-4 py-4 text-left font-semibold text-gray-600">Address</th>
                  <th className="px-4 py-4 text-left font-semibold text-gray-600">Date</th>
                  <th className="px-4 py-4 text-left font-semibold text-gray-600">Network</th>
                  <th className="px-4 py-4 text-right font-semibold text-gray-600">Amount</th>
                </tr>
              </thead>
              <tbody>
                {sortedFunders
                  .slice(0, seeAll ? undefined : 5)
                  ?.map((item, index) => {
                    let funder = item?.funder?.public_address || item?.funder;
                    const medal = getMedalIcon(index);
                    
                    return (
                      <tr
                        key={index}
                        className={`hover:bg-gray-50 transition-colors ${
                          index < 3 ? 'bg-gradient-to-r from-primary/5 to-transparent' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                        }`}
                      >
                        {/* Rank */}
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center">
                            {medal ? (
                              <span className="text-2xl">{medal}</span>
                            ) : (
                              <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-medium">
                                {index + 1}
                              </span>
                            )}
                          </div>
                        </td>
                        
                        {/* Contributor */}
                        <td className="px-4 py-4">
                          <div
                            onClick={() =>
                              !item?.isAnonymous &&
                              item?.funder?.role !== UN_REGISTERED &&
                              typeof item?.funder !== "string" &&
                              navigateToProfile(item?.funder?._id, item?.funder?.role)
                            }
                            className={`flex items-center gap-3 ${
                              !item?.isAnonymous && typeof item?.funder !== "string" 
                                ? "cursor-pointer group" 
                                : "cursor-default"
                            }`}
                          >
                            <img
                              src={item?.funder?.images?.profile || UserPlaceHolderImg}
                              alt=""
                              className={`w-10 h-10 rounded-full border-2 border-white shadow-md transition-transform ${
                                !item?.isAnonymous ? "group-hover:scale-110" : "opacity-60"
                              }`}
                            />
                            <div className="flex flex-col">
                              <span className={`font-medium transition-colors ${
                                item?.isAnonymous 
                                  ? "text-gray-500" 
                                  : "text-secondary group-hover:text-primary"
                              }`}>
                                {item?.isAnonymous
                                  ? item?.displayName || "Anonymous"
                                  : typeof item?.funder === "string"
                                  ? "Anonymous"
                                  : item?.funder?.role === "applicant"
                                  ? `${item?.funder?.firstname} ${item?.funder?.lastname}`
                                  : item?.funder?.fundername}
                              </span>
                              {item?.isAnonymous && (
                                <span className="text-xs text-gray-400">(Anonymous)</span>
                              )}
                            </div>
                          </div>
                        </td>
                        
                        {/* Token ID */}
                        <td className="px-4 py-4">
                          <ExternalLink
                            href={
                              item?.blockchain === "arbitrum"
                                ? `${contractNetworks?.[item?.blockchain]?.explorer}/token/${contractNetworks?.[item?.blockchain]?.fundrasingMinter}?a=${item?.tokenId}`
                                : `${contractNetworks?.[item?.blockchain]?.explorer}/nft/${contractNetworks?.[item?.blockchain]?.fundrasingMinter}/${item?.tokenId}`
                            }
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
                          >
                            #{item?.tokenId}
                            <HiOutlineExternalLink className="w-3 h-3" />
                          </ExternalLink>
                        </td>
                        
                        {/* Address */}
                        <td className="px-4 py-4">
                          <ExternalLink
                            href={`${contractNetworks?.[item?.blockchain]?.explorer}/address/${funder}`}
                            className="text-gray-600 hover:text-primary transition-colors font-mono text-sm"
                          >
                            <WalletDisplay address={funder} />
                          </ExternalLink>
                        </td>
                        
                        {/* Date */}
                        <td className="px-4 py-4 text-gray-600 text-sm">
                          <ExternalLink
                            href={`${contractNetworks?.[item?.blockchain]?.explorer}/tx/${item?.txHash}`}
                            className="hover:text-primary transition-colors"
                          >
                            {new Date(item?.updatedAt).toLocaleDateString()}
                          </ExternalLink>
                        </td>
                        
                        {/* Network */}
                        <td className="px-4 py-4">
                          <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                            {getChain({ network: item?.blockchain })?.name}
                          </span>
                        </td>
                        
                        {/* Amount */}
                        <td className="px-4 py-4 text-right">
                          <div className="flex flex-col items-end">
                            <span className="font-bold text-secondary">
                              {item?.amount} {item?.token}
                            </span>
                            <span className="text-sm text-gray-500">
                              ≈ ${toDollar({
                                amount: item?.amount,
                                token: item?.token,
                                decimal: 2,
                              })}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {funders?.length > 5 && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setSeeAll(!seeAll)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary/10 text-primary font-medium hover:bg-primary/20 transition-colors"
              >
                {seeAll ? "Show less" : `Show all ${funders.length} contributors`}
                <BsChevronDown
                  className={`transition-transform duration-300 ${seeAll ? "rotate-180" : ""}`}
                />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Funders;
